<template>
  <div class="min-h-screen bg-[var(--p-surface-50)]">
    <!-- Mobile overlay -->
    <div
      v-if="mobileOpen"
      class="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
      @click="mobileOpen = false"
    />

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200/80',
        'w-[260px] bg-white h-screen',
        'transition-transform duration-300 ease-out',
        'lg:translate-x-0',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <!-- Logo -->
      <div class="flex items-center justify-center px-6 h-[72px] border-b border-gray-100 shrink-0">
        <img :src="$logo" :alt="$appName" class="h-9 w-auto" />
      </div>

      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto px-3 py-5">
        <template v-for="(group, gi) in navGroups" :key="group.group">
          <div v-if="gi > 0" class="mt-4" />
          <div v-if="group.heading" class="mb-2 px-3">
            <span class="text-[10px] font-semibold tracking-widest uppercase text-gray-400">{{ group.heading }}</span>
          </div>

          <router-link
            v-for="item in group.pages"
            :key="item.route"
            :to="item.route"
            :class="[
              'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] font-medium transition-all duration-150 mb-0.5',
              isActive(item.route)
                ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            ]"
            @click="mobileOpen = false"
          >
            <span
              :class="[
                'flex items-center justify-center w-8 h-8 rounded-lg transition-colors duration-150',
                isActive(item.route) ? 'bg-[var(--color-primary-glow)]' : 'bg-gray-100 group-hover:bg-gray-200/60'
              ]"
              v-html="item.icon"
            />
            {{ item.heading }}
          </router-link>
        </template>
      </nav>

      <!-- User panel -->
      <div class="shrink-0 border-t border-gray-100 px-3 py-3 relative">
        <button
          @click="userMenuOpen = !userMenuOpen"
          class="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <div class="w-9 h-9 rounded-full bg-[var(--color-primary-light)] text-[var(--color-primary)] flex items-center justify-center text-xs font-semibold shrink-0">
            {{ userInitials }}
          </div>
          <div class="flex-1 min-w-0 text-left">
            <p class="text-sm font-medium text-gray-900 truncate">{{ displayName }}</p>
            <p class="text-[11px] text-gray-400 truncate">{{ user?.email }}</p>
          </div>
          <svg
            :class="['w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0', userMenuOpen ? 'rotate-180' : '']"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        <!-- User dropdown menu -->
        <Transition name="user-menu">
          <div
            v-if="userMenuOpen"
            class="absolute bottom-full left-3 right-3 mb-2 bg-white rounded-xl border border-gray-200 shadow-xl shadow-gray-200/50 overflow-hidden z-50"
          >
            <!-- Header -->
            <div class="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ displayName }}</p>
              <p class="text-xs text-gray-400 truncate mt-0.5">{{ user?.email }}</p>
              <p v-if="isAdmin" class="mt-1.5">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                  Admin
                </span>
              </p>
            </div>

            <!-- Menu items -->
            <div class="py-1.5">
              <button
                @click="goToProfile"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <svg class="w-[18px] h-[18px] text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Profile Settings
              </button>
              <button
                @click="goToDeveloper"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <svg class="w-[18px] h-[18px] text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
                Developer
              </button>
              <div class="mx-3 my-1 border-t border-gray-100" />
              <button
                @click="logout"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
              >
                <svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Logout
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </aside>

    <!-- Main content area -->
    <div class="lg:ml-[260px] h-screen flex flex-col">
      <!-- Top bar (mobile) -->
      <header class="lg:hidden flex items-center justify-between h-14 px-4 bg-white border-b border-gray-200/80 shrink-0">
        <button
          @click="mobileOpen = true"
          class="p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <img :src="$logo" :alt="$appName" class="h-6 w-auto" />
        <div class="w-9"/>
      </header>

      <!-- Page content -->
      <main ref="mainScroll" class="flex-1 overflow-y-auto bg-[var(--p-surface-50)]">
        <!-- Sticky page header -->
        <div v-if="pageTitle" class="sticky top-0 z-10 bg-[var(--p-surface-50)]">
          <div class="px-6 pt-6 pb-4">
            <h1 class="text-2xl font-semibold text-gray-900 tracking-tight">{{ pageTitle }}</h1>
            <p v-if="pageDescription" class="text-sm text-gray-400 mt-1">{{ pageDescription }}</p>
          </div>
          <div class="h-px bg-gradient-to-r from-gray-200/60 via-gray-200/30 to-transparent mx-6" />
        </div>

        <!-- Route content -->
        <div class="p-6">
          <router-view />
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import sidebarMenu from '../menu/sidebar-menu.js'
import UserService from '../services/user-service.js'

export default {
  name: 'DashboardLayout',

  data() {
    return {
      mobileOpen: false,
      userMenuOpen: false,
      navGroups: []
    }
  },

  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    displayName() {
      if (!this.user) return ''
      return this.user.name || this.user.email?.split('@')[0] || ''
    },
    userInitials() {
      const name = this.displayName
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },
    pageTitle() {
      const matched = [...this.$route.matched].reverse()
      for (const r of matched) {
        if (r.meta?.title) return r.meta.title
      }
      return ''
    },
    pageDescription() {
      const matched = [...this.$route.matched].reverse()
      for (const r of matched) {
        if (r.meta?.description) return r.meta.description
      }
      return ''
    },
  },

  watch: {
    '$route.path'() {
      this.$nextTick(() => {
        if (this.$refs.mainScroll) this.$refs.mainScroll.scrollTop = 0
      })
    }
  },

  created() {
    this.navGroups = sidebarMenu
      .map(group => ({
        ...group,
        pages: group.pages.filter(page => {
          if (page.adminOnly) return UserService.isAdmin()
          if (!page.permissions?.length) return true
          return UserService.hasPermission(page.permissions)
        })
      }))
      .filter(group => group.pages.length > 0)
  },

  mounted() {
    document.addEventListener('click', this.onClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.onClickOutside)
  },

  methods: {
    isActive(route) {
      return this.$route.path === route || this.$route.path.startsWith(route + '/')
    },
    goToProfile() {
      this.userMenuOpen = false
      this.$router.push('/profile')
    },
    goToDeveloper() {
      this.userMenuOpen = false
      this.$router.push('/developer')
    },
    logout() {
      this.userMenuOpen = false
      this.$store.dispatch('auth/logout')
      this.$router.push('/login')
    },
    onClickOutside(e) {
      if (this.userMenuOpen && !e.target.closest('.shrink-0.border-t')) {
        this.userMenuOpen = false
      }
    }
  }
}
</script>

<style scoped>
.user-menu-enter-active,
.user-menu-leave-active {
  transition: all 0.15s ease;
}
.user-menu-enter-from,
.user-menu-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
